import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { AgencyService } from '../../../core/agency/agency.service';
import { Agency, AgencyInvitation, AgencyMember } from '../../../core/agency/agency.types';
import { AuthService } from '../../../core/auth/auth.service';
import { IconComponent } from '../../../shared/icon/icon';
import { readError } from '../../../core/http/read-error';

type PageState = 'loading' | 'ready' | 'no-agency' | 'forbidden' | 'error';

@Component({
  selector: 'app-agent-team',
  imports: [RouterLink, IconComponent, ReactiveFormsModule],
  templateUrl: './agent-team.html',
  styleUrl: './agent-team.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentTeamComponent implements OnInit {
  private readonly agencyService = inject(AgencyService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  protected readonly user = this.auth.currentUser;
  protected readonly pageState = signal<PageState>('loading');
  protected readonly agency = signal<Agency | null>(null);

  protected readonly showInvite = signal(false);
  protected readonly inviteError = signal<string | null>(null);
  protected readonly inviteSubmitting = signal(false);
  protected readonly inviteForm = this.fb.group({
    first_name: [''],
    last_name: [''],
    email: ['', [Validators.required, Validators.email]],
    role: ['AGENT' as 'ADMIN' | 'AGENT'],
  });

  protected readonly members = computed(() => this.agency()?.members ?? []);
  protected readonly pendingInvites = computed(() => this.agency()?.pending_invitations ?? []);

  ngOnInit(): void {
    this.agencyService
      .getMine()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (agency) => {
          if (agency.member_role !== 'ADMIN') {
            // Staff don't manage the team — bounce them back.
            this.pageState.set('forbidden');
            this.cdr.markForCheck();
            return;
          }
          this.agency.set(agency);
          this.pageState.set('ready');
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.pageState.set(err?.status === 404 ? 'no-agency' : 'error');
          this.cdr.markForCheck();
        },
      });
  }

  protected toggleInvite(): void {
    this.showInvite.update((v) => !v);
    this.inviteError.set(null);
    if (!this.showInvite()) {
      this.inviteForm.reset({ first_name: '', last_name: '', email: '', role: 'AGENT' });
    }
  }

  protected submitInvite(): void {
    if (this.inviteForm.invalid || this.inviteSubmitting()) {
      this.inviteForm.markAllAsTouched();
      return;
    }
    this.inviteSubmitting.set(true);
    this.inviteError.set(null);
    const value = this.inviteForm.getRawValue();
    this.agencyService
      .inviteMember({
        email: value.email!,
        first_name: value.first_name ?? '',
        last_name: value.last_name ?? '',
        role: value.role ?? 'AGENT',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (agency) => {
          this.agency.set(agency);
          this.inviteSubmitting.set(false);
          this.showInvite.set(false);
          this.inviteForm.reset({ first_name: '', last_name: '', email: '', role: 'AGENT' });
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.inviteSubmitting.set(false);
          this.inviteError.set(
            readError(
              err,
              'Could not send the invitation. Please check the details and try again.',
            ),
          );
          this.cdr.markForCheck();
        },
      });
  }

  protected changeRole(member: AgencyMember, role: 'ADMIN' | 'AGENT'): void {
    this.agencyService
      .setMemberRole(member.short_id, role)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (agency) => {
          this.agency.set(agency);
          this.cdr.markForCheck();
        },
      });
  }

  protected removeMember(member: AgencyMember): void {
    if (!confirm(`Remove ${member.name || member.email} from the team?`)) return;
    this.agencyService
      .removeMember(member.short_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (agency) => {
          this.agency.set(agency);
          this.cdr.markForCheck();
        },
      });
  }

  protected cancelInvite(invite: AgencyInvitation): void {
    if (!confirm(`Cancel the invitation to ${invite.email}?`)) return;
    this.agencyService
      .cancelInvitation(invite.short_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (agency) => {
          this.agency.set(agency);
          this.cdr.markForCheck();
        },
      });
  }

  protected isOnlyAdmin(member: AgencyMember): boolean {
    if (member.role !== 'ADMIN') return false;
    return this.members().filter((m) => m.role === 'ADMIN').length <= 1;
  }

  protected isSelf(member: AgencyMember): boolean {
    return member.email.toLowerCase() === (this.user()?.email ?? '').toLowerCase();
  }
}
